import { type FC, useCallback, useEffect, useState } from 'react'
import { Outlet } from 'react-router-dom'
import type { Interaction } from './interaction.js'
import { useInteractionConfig } from './useInteractionConfig.js'
import { useGetProfileCompletedInteractions } from '../profile/model/profile/hooks.js'
import { isFeatureEnabled } from '../config/index.js'

export const InteractionManager: FC = () => {
  const [currentInteraction, setCurrentInteraction] = useState<Interaction | null>(null)
  const { isFetched } = useGetProfileCompletedInteractions()
  const { interactions: configuredInteractions } = useInteractionConfig()
  const isEnabled = isFeatureEnabled('interaction')

  /**
   * Based on the order from the configuration, we determine what the next interaction should be.
   * The provided `shouldExecute` function is used to assess whether the respective interaction should be displayed.
   * If the function does not yield a result, we proceed to the next interaction in the configuration.
   */
  const determineCurrentInteraction = useCallback(async () => {
    const result = await configuredInteractions.reduce(async (interaction, action) => {
      const resolvedInteraction = await interaction
      if (resolvedInteraction) {
        return resolvedInteraction
      }
      const shouldExecute = await action.shouldExecute()
      return shouldExecute ? action : null
    }, Promise.resolve(null))

    setCurrentInteraction(result)
  }, [configuredInteractions])

  /**
   * To determine if we can search for the next interaction, we must first ensure that the data for previously
   * `completedInteractions` has been retrieved from the API. Additionally, we need to check that we are on the
   * correct page to allow the activation to be triggered. Furthermore, we must verify that no interaction has already
   * been displayed, as we aim to show only one interaction per session visit.
   */
  useEffect(() => {
    if (isFetched && isEnabled && currentInteraction === null) {
      determineCurrentInteraction()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched, isEnabled, currentInteraction])

  const Component = currentInteraction?.component

  return (
    <>
      <Outlet />
      {Component}
    </>
  )
}
